import { api } from '@features';

import type {
  ETransactionKind,
  ETransactionStatus,
  ETransactionVerification,
  ITransaction,
  ITransactionSummaryRow,
} from './Transaction';

interface ITransactionsResponse {
  transactions: ITransaction[];
  success: boolean;
  totalPages: number;
  currentPage: number;
  limit: number;
  total: number;
}

interface ITransactionSummaryResponse {
  success: boolean;
  summary: ITransactionSummaryRow[];
  openDiscrepancies: number;
}

interface ISetVerificationResponse {
  success: boolean;
  transaction: ITransaction;
}

export interface IListTransactionsArgs {
  page?: number;
  limit?: number;
  kind?: ETransactionKind | '';
  status?: ETransactionStatus | '';
  verification?: ETransactionVerification | '';
  steamId?: string;
  /**
   * Only rows created at least this long ago. Paired with status=initiated this is the
   * stranded purchase query -- begun, never resolved, old enough not to be in flight.
   */
  olderThanMinutes?: number;
  from?: string;
  to?: string;
}

export const transactionApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getTransactions: builder.query<ITransactionsResponse, IListTransactionsArgs>({
      query: ({ page = 1, limit = 50, kind, status, verification, steamId, olderThanMinutes, from, to }) => {
        let url = `/transaction?page=${page}&limit=${limit}`;
        if (kind) url += `&kind=${kind}`;
        if (status) url += `&status=${status}`;
        if (verification) url += `&verification=${verification}`;
        if (steamId) url += `&steamId=${encodeURIComponent(steamId)}`;
        if (olderThanMinutes) url += `&olderThanMinutes=${olderThanMinutes}`;
        if (from) url += `&from=${encodeURIComponent(from)}`;
        if (to) url += `&to=${encodeURIComponent(to)}`;
        return {
          url,
          method: 'GET',
        };
      },
      providesTags: [{ type: 'Transaction' }],
      transformResponse: (response: ITransactionsResponse) => {
        if (response.success) {
          return response;
        }
        return Promise.reject('Unauthorized');
      },
      transformErrorResponse: (response) => {
        if (response.status === 401) {
          return 'Unauthorized';
        }
        return response;
      },
    }),
    getTransactionSummary: builder.query<ITransactionSummaryResponse, { from?: string; to?: string }>({
      query: ({ from, to }) => {
        let url = '/transaction/summary';
        const params: string[] = [];
        if (from) params.push(`from=${encodeURIComponent(from)}`);
        if (to) params.push(`to=${encodeURIComponent(to)}`);
        if (params.length) url += `?${params.join('&')}`;
        return {
          url,
          method: 'GET',
        };
      },
      providesTags: [{ type: 'Transaction' }],
      transformResponse: (response: ITransactionSummaryResponse) => {
        if (response.success) {
          return response;
        }
        return Promise.reject('Unauthorized');
      },
      transformErrorResponse: (response) => {
        if (response.status === 401) {
          return 'Unauthorized';
        }
        return response;
      },
    }),
    /**
     * Records a human's verification decision on one row.
     *
     * Admin and developer only, and deliberately narrow: it writes verification, verifiedAt
     * and verificationNote. It grants nothing -- acting on a discrepancy still means going
     * and granting the credits in LootLocker.
     */
    setTransactionVerification: builder.mutation<
      ISetVerificationResponse,
      { id: string; verification: ETransactionVerification; note?: string }
    >({
      query: ({ id, verification, note }) => ({
        url: `/transaction/${id}/verification`,
        method: 'PATCH',
        body: { verification, note },
      }),
      // The summary carries the open discrepancy count the queue button reads, so both the
      // listing and the badge have to refetch.
      invalidatesTags: [{ type: 'Transaction' }],
      transformErrorResponse: (response) => {
        if (response.status === 401) {
          return 'Unauthorized';
        }
        return response;
      },
    }),
  }),
});

export const {
  useGetTransactionsQuery,
  useGetTransactionSummaryQuery,
  useSetTransactionVerificationMutation,
} = transactionApi;
